import { useState, useEffect } from 'react';
import { 
  Calendar as CalendarComponent, 
  Views, 
  dayjsLocalizer, 
  type SlotInfo, 
  type Event 
} from 'react-big-calendar';
import dayjs from 'dayjs';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Plus, Calendar as CalendarIcon, Clock, Users, Trash, Check, X } from 'lucide-react';
import CalendarService from '@/api/calendar.service';
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

// Set up localizer with dayjs
const localizer = dayjsLocalizer(dayjs);

// Event form schema
const eventFormSchema = z.object({
  title: z.string().min(2, 'Title is required'),
  description: z.string().optional(),
  start: z.string().min(1, 'Start date is required'),
  end: z.string().min(1, 'End date is required'),
  location: z.string().optional(),
  color: z.string().default('#3174ad'),
  allDay: z.boolean().default(false)
});

type EventFormValues = z.infer<typeof eventFormSchema>;

// Calendar event type
interface CalendarEvent extends Event {
  id: number;
  title: string;
  description?: string;
  location?: string;
  color?: string;
  start: Date;
  end: Date;
  allDay?: boolean;
}

export default function Calendar() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState(() => {
    const storedBusiness = localStorage.getItem('selectedBusiness');
    return storedBusiness ? JSON.parse(storedBusiness).id : null;
  });
  const [selectedSlot, setSelectedSlot] = useState<SlotInfo | null>(null);
  const { toast } = useToast();

  // Initialize form
  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: {
      title: '',
      description: '',
      start: '',
      end: '',
      location: '',
      color: '#3174ad',
      allDay: false
    }
  });

  // Load events on component mount
  useEffect(() => {
    if (selectedBusinessId) {
      fetchEvents();
    }
  }, [selectedBusinessId]);

  // Fetch events from API
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await CalendarService.getAllEvents(selectedBusinessId);
      
      // Map API events to calendar events
      const mappedEvents = response.data.map((event: any) => ({
        id: event.id,
        title: event.title,
        description: event.description,
        start: new Date(event.startDate),
        end: new Date(event.endDate),
        location: event.location,
        color: event.color || '#3174ad',
        allDay: event.allDay
      }));
      
      setEvents(mappedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast({
        title: 'Error',
        description: 'Failed to load calendar events',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle slot selection for creating a new event
  const handleSelectSlot = (slotInfo: SlotInfo) => {
    setSelectedSlot(slotInfo);
    
    // Pre-fill form with selected time slot
    form.reset({
      title: '',
      description: '',
      start: dayjs(slotInfo.start).format('YYYY-MM-DDTHH:mm'),
      end: dayjs(slotInfo.end).format('YYYY-MM-DDTHH:mm'),
      location: '',
      color: '#3174ad',
      allDay: slotInfo.slots.length > 1 // Check if multiple slots are selected
    });
    
    setIsCreateModalOpen(true);
  };

  // Handle event selection for viewing details
  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsViewModalOpen(true);
  };

  // Handle form submission for creating a new event
  const onSubmit = async (data: EventFormValues) => {
    try {
      const eventData = {
        title: data.title,
        description: data.description || '',
        startDate: data.start,
        endDate: data.end,
        location: data.location || '',
        color: data.color,
        allDay: data.allDay
      };
      
      const response = await CalendarService.createEvent(selectedBusinessId, eventData);
      
      if (response.data) {
        // Add new event to state
        const newEvent: CalendarEvent = {
          id: response.data.id,
          title: data.title,
          description: data.description,
          start: new Date(data.start),
          end: new Date(data.end),
          location: data.location,
          color: data.color,
          allDay: data.allDay
        };
        
        setEvents([...events, newEvent]);
        
        toast({
          title: 'Success',
          description: 'Event created successfully'
        });
        
        setIsCreateModalOpen(false);
        form.reset();
      }
    } catch (error) {
      console.error('Error creating event:', error);
      toast({
        title: 'Error',
        description: 'Failed to create event',
        variant: 'destructive'
      });
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      await CalendarService.deleteEvent(selectedEvent.id);
      
      // Remove deleted event from state
      setEvents(events.filter(event => event.id !== selectedEvent.id));
      
      toast({
        title: 'Success',
        description: 'Event deleted successfully'
      });
      
      setIsDeleteConfirmOpen(false);
      setIsViewModalOpen(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete event',
        variant: 'destructive'
      });
    }
  };

  // Custom event styling
  const eventStyleGetter = (event: CalendarEvent) => {
    return {
      style: {
        backgroundColor: event.color || '#3174ad',
        borderRadius: '4px',
        color: '#fff',
        border: 'none'
      }
    };
  };

  return (
    <div className="container py-6 mx-auto">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Calendar</h1>
            <p className="text-muted-foreground mt-1">
              Manage your schedule and appointments
            </p>
          </div>
          
          <Button 
            onClick={() => {
              form.reset({
                title: '',
                description: '',
                start: dayjs().format('YYYY-MM-DDTHH:mm'),
                end: dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
                location: '',
                color: '#3174ad',
                allDay: false
              });
              setIsCreateModalOpen(true);
            }}
            className="gap-1"
          >
            <Plus className="w-4 h-4" />
            Create Event
          </Button>
        </div>
        
        {/* Calendar */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Your Schedule</CardTitle>
            <CardDescription>
              View and manage your events and appointments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ height: 700 }}>
              <CalendarComponent
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable
                eventPropGetter={eventStyleGetter}
                defaultView={Views.WEEK}
                views={['month', 'week', 'day', 'agenda']}
              />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Create Event Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create New Event</DialogTitle>
            <DialogDescription>
              Add a new event to your calendar
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Event Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Meeting with client" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="start"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date & Time</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="end"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date & Time</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Event details..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input placeholder="Office Room 101" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Color</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            {...field}
                            className="w-10 h-10 p-1"
                          />
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select color" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="#3174ad">Blue</SelectItem>
                              <SelectItem value="#32a852">Green</SelectItem>
                              <SelectItem value="#a83232">Red</SelectItem>
                              <SelectItem value="#a78732">Orange</SelectItem>
                              <SelectItem value="#7d32a7">Purple</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="allDay"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-end space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>All Day Event</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Create Event</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      {/* View Event Modal */}
      {selectedEvent && (
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{selectedEvent.title}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <CalendarIcon className="w-4 h-4" />
                <span>
                  {dayjs(selectedEvent.start).format('MMM D, YYYY')}
                  {selectedEvent.allDay ? ' (All day)' : 
                    ` • ${dayjs(selectedEvent.start).format('h:mm A')} - ${dayjs(selectedEvent.end).format('h:mm A')}`}
                </span>
              </div>
              
              {selectedEvent.location && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>{selectedEvent.location}</span>
                </div>
              )}
              
              {selectedEvent.description && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="text-sm font-medium mb-2">Description</h4>
                  <p className="text-sm text-muted-foreground">{selectedEvent.description}</p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                className="gap-1"
                onClick={() => {
                  setIsDeleteConfirmOpen(true);
                }}
              >
                <Trash className="w-4 h-4" />
                Delete
              </Button>
              <Button onClick={() => setIsViewModalOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this event? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="gap-1"
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteEvent}
              className="gap-1"
            >
              <Check className="w-4 h-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 